/* eslint-disable camelcase */
import React, { useCallback, useEffect, useState, useRef } from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CloseIcon2 from '~/icons/components/CloseIcon2/CloseIcon2';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { useGridFacets } from '../../context/GridDataContexts';
import {
  useFilterAvailability,
  useStagedFilters,
} from '../../context/PendingFiltersContext';
import {
  getNewQueryParamString,
  QueryParamBehavior,
} from '../../utils/routing';
import SortDropdown from '../SortDropdown';

import FilterDropdown from './FilterDropdown';

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.gridV2FiltersPanelHeaderText};
  font-size: 18px;
  font-weight: 700;
  line-height: ${24 / 18};
  margin-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.neutralLight};
  padding: 16px;
  flex-shrink: 0;
`;

const PanelHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FilterCountBadge = styled.span`
  color: ${({ theme }) => theme.colors.gridV2FiltersPanelHeaderCount};
  font-size: 16px;
  font-weight: 350;
`;

const FacetList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
  padding-bottom: 128px;
  list-style: none;
  flex: 1;
  overflow-y: auto;

  /* Hide scrollbar */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`;

const BlinkingDash = styled.span`
  @keyframes slowBlink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.2;
    }
  }
  animation: slowBlink 1.4s ease-in-out infinite;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  flex-shrink: 0;
  position: relative;
  z-index: 4;
`;

const ClearAllButton = styled.button<{ $isVisible?: boolean }>`
  display: flex;
  height: 48px;
  align-items: center;
  gap: 8px;
  justify-content: center;
  padding: 0 16px;
  flex: ${({ $isVisible }) => ($isVisible === false ? '0' : '1')};
  overflow: hidden;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.9);
  color: ${({ theme }) => theme.colors.palette.shadow};
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-transform: uppercase;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.palette.shadow};
  transition:
    color 0.2s ease,
    border-color 0.2s ease,
    filter 0.2s ease,
    flex 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${({ $isVisible }) =>
      $isVisible === false ? 'fadeDown' : 'fadeUp'}
    0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  position: relative;

  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeDown {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(10px);
    }
  }

  &:hover {
    filter: brightness(0.95);
  }

  &:active {
    filter: brightness(0.9);
  }
`;

const ApplyButton = styled.button<{ $hasFilters?: boolean }>`
  display: flex;
  height: 48px;
  align-items: center;
  gap: 8px;
  justify-content: center;
  flex: ${({ $hasFilters }) => ($hasFilters ? '1' : '1')};
  flex-shrink: 0;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.palette.shadow};
  color: ${({ theme }) => theme.colors.palette.white};
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-transform: uppercase;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid transparent;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease,
    flex 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: ${({ theme }) => theme.colors.palette.shadow90};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.palette.shadow80};
  }

  :disabled {
    background-color: ${({ theme }) => theme.colors.palette.shadow10};
    border-color: ${({ theme }) => theme.colors.palette.shadow30};
    color: ${({ theme }) => theme.colors.palette.shadow65};
    cursor: default;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  bottom: 56px;
  left: 0;
  right: 0;
  height: 160px;
  background: linear-gradient(to bottom, transparent, white);
  pointer-events: none;
  z-index: 1;
`;

const StyledCloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ApplyButtonLabel = ({
  resultCount,
  isDynamicQueryLoading,
}: {
  resultCount: number | null;
  isDynamicQueryLoading: boolean;
}) => {
  // Products V2 grids don't support lean facet queries yet, so resultCount
  // is null — fall back to a label without a count.
  if (typeof resultCount !== 'number') {
    return (
      <FormattedMessage id="filtersPanel.apply" defaultMessage="Show Results" />
    );
  }

  const count = isDynamicQueryLoading ? (
    <BlinkingDash> — </BlinkingDash>
  ) : (
    resultCount
  );

  return (
    <FormattedMessage
      id="filtersPanel.applyWithCount"
      defaultMessage="Show Results [{count}]"
      values={{ count }}
    />
  );
};

const FiltersPanel = ({
  closeFiltersPanel = () => {},
  showSortDropdown = false,
  activeFacetName = null,
}: {
  closeFiltersPanel?: () => void;
  showSortDropdown?: boolean;
  activeFacetName?: string | null;
}) => {
  const router = useRouter();

  const { facets: productQueryFacets } = useGridFacets();

  const {
    stagedFilters,
    clearAllFilters,
    resetToUrl,
    stagedSort,
    setStagedSort,
  } = useStagedFilters();

  const {
    isDynamicQueryLoading,
    baseFacets,
    baseTotalNumResults,
    dynamicTotalNumResults,
  } = useFilterAvailability();

  // Use base facets (unfiltered) when available so the panel always shows the
  // full set of options regardless of which filters are currently applied.
  // Fall back to product query facets when the lean query hasn't loaded yet.
  const facets = baseFacets ?? productQueryFacets;

  // Products V2 grids don't have dynamic filter data yet (no lean facet query),
  // so we only show the result count for CIO grids where baseFacets are available.
  // When no staged filters are active (e.g. after Clear All), dynamicTotalNumResults
  // is null — fall back to baseTotalNumResults (unfiltered total).
  const resultCount = baseFacets
    ? (dynamicTotalNumResults ?? baseTotalNumResults ?? 0)
    : null;

  const [shouldShowFilters, setShouldShowFilters] = useState(false);
  const [isFiltersVisible, setIsFiltersVisible] = useState(true);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const selectedFacetsChipsCount = Object.values(stagedFilters).reduce<number>(
    (acc, value) => acc + ((value as string[])?.length || 0),
    0
  );

  const handleClose = useCallback(() => {
    resetToUrl();
    closeFiltersPanel();
  }, [resetToUrl, closeFiltersPanel]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose]);

  useEffect(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    if (selectedFacetsChipsCount > 0) {
      setShouldShowFilters(true);
      setIsFiltersVisible(true);
    } else {
      setIsFiltersVisible(false);
      animationTimeoutRef.current = setTimeout(() => {
        setShouldShowFilters(false);
      }, 300);
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [selectedFacetsChipsCount]);

  const handleApplyFilters = () => {
    const [aspathname, query] = router.asPath.split('?');
    const newQueryParamString = getNewQueryParamString({
      query,
      newQueryParams: {
        ...stagedFilters,
        ...(showSortDropdown && stagedSort ? { sort: stagedSort } : {}),
      },
      queryParamBehavior: QueryParamBehavior.REPLACE,
    });

    if (!newQueryParamString) {
      router.replace(router.pathname, aspathname, {
        shallow: true,
      });
      closeFiltersPanel();
      return;
    }

    router.replace(router.pathname, `${aspathname}?${newQueryParamString}`, {
      shallow: true,
    });

    closeFiltersPanel();
  };

  const handleClearAll = () => {
    clearAllFilters();
  };

  return (
    <StyledContainer>
      <PanelHeader>
        <PanelHeaderTitle>
          <FormattedMessage id="filtersPanel.title" defaultMessage="FILTERS" />
          {selectedFacetsChipsCount > 0 && (
            <FilterCountBadge>[{selectedFacetsChipsCount}]</FilterCountBadge>
          )}
        </PanelHeaderTitle>
        <StyledCloseButton onClick={handleClose}>
          <CloseIcon2 width={16} height={16} />
        </StyledCloseButton>
      </PanelHeader>

      <FacetList>
        {showSortDropdown && (
          <SortDropdown stagedSort={stagedSort} onSortSelect={setStagedSort} />
        )}
        {facets?.map((facet) => (
          <li key={facet?.name}>
            <FilterDropdown
              {...facet}
              isActive={facet?.name === activeFacetName}
            />
          </li>
        ))}
      </FacetList>

      <ButtonContainer>
        {shouldShowFilters && (
          <ClearAllButton
            type="button"
            onClick={handleClearAll}
            $isVisible={isFiltersVisible}
            data-autotag="filter-clear-all"
          >
            <FormattedMessage
              id="filtersPanel.clearAll"
              defaultMessage="Clear All"
            />
          </ClearAllButton>
        )}
        <ApplyButton
          type="button"
          onClick={handleApplyFilters}
          $hasFilters={shouldShowFilters}
          data-autotag="filter-apply"
        >
          <ApplyButtonLabel
            resultCount={resultCount}
            isDynamicQueryLoading={isDynamicQueryLoading}
          />
        </ApplyButton>
      </ButtonContainer>
      <GradientOverlay />
    </StyledContainer>
  );
};

FiltersPanel.propTypes = {
  activeFacetName: PropTypes.string,
  closeFiltersPanel: PropTypes.func,
  showSortDropdown: PropTypes.bool,
};

export default FiltersPanel;
